import axios from "axios";

/**
 * Generate a post using the provided options
 * @param {Object} options - Post generation options
 * @param {string} userId - User ID
 * @returns {Promise} - API response
 */
export const generatePost = async (options, userId) => {
  // Thiết lập timeout dài hơn cho các request lớn (2 phút)
  const timeout = 120000;

  try {
    // Kiểm tra xem options có chứa contextFiles có kích thước lớn không
    if (options.contextFiles && Array.isArray(options.contextFiles)) {
      // Đếm tổng kích thước của tất cả files
      let totalSize = 0;
      options.contextFiles.forEach((file) => {
        if (file.content && typeof file.content === "string") {
          totalSize += file.content.length;
        }
      });

      // Giới hạn kích thước tối đa là 10MB (10 * 1024 * 1024 ≈ 10,485,760 bytes)
      // Nhưng vì base64 encoding tăng kích thước khoảng 33%, nên giới hạn thực tế là khoảng 7.5MB
      const maxTotalSize = 10 * 1024 * 1024; // 10MB
      const maxSingleFileSize = 2 * 1024 * 1024; // 2MB cho mỗi file

      // Nếu tổng kích thước quá lớn (>10MB), cảnh báo và giới hạn
      if (totalSize > maxTotalSize) {
        console.warn(
          `Tổng kích thước file (${(totalSize / (1024 * 1024)).toFixed(
            2
          )}MB) vượt quá giới hạn 10MB, sẽ lọc và giới hạn số lượng file`
        );

        // Sắp xếp files theo kích thước tăng dần để ưu tiên các file nhỏ
        const sortedFiles = [...options.contextFiles].sort((a, b) => {
          const sizeA =
            a.content && typeof a.content === "string" ? a.content.length : 0;
          const sizeB =
            b.content && typeof b.content === "string" ? b.content.length : 0;
          return sizeA - sizeB;
        });

        // Lọc các file quá lớn và giới hạn số lượng tối đa 5 file
        const limitedContextFiles = sortedFiles
          .filter(
            (file) =>
              !file.content ||
              (typeof file.content === "string" &&
                file.content.length < maxSingleFileSize)
          )
          .slice(0, 5); // Chỉ giữ tối đa 5 file

        options = {
          ...options,
          contextFiles: limitedContextFiles,
        };
      }
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/content/generate-post`,
      {
        options,
        userId,
      },
      {
        timeout,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("33333333333");
    console.log(response.data);

    // Xử lý kết quả để đảm bảo tương thích với cấu trúc mới
    if (response.data && response.data.success) {
      // Kiểm tra nếu response có định dạng mới (mảng posts)
      if (response.data.posts && Array.isArray(response.data.posts)) {
        return response;
      }
      // Nếu response có định dạng cũ (title, content)
      else if (
        response.data.data &&
        (response.data.data.title || response.data.data.content)
      ) {
        // Chuyển đổi sang định dạng mới
        console.log(
          "Nhận được phản hồi định dạng cũ, chuyển đổi sang định dạng mới"
        );
        const oldData = response.data.data;
        response.data.data = {
          posts: [
            {
              title: oldData.title || "",
              content: oldData.content || "",
              imageUrls: oldData.imageUrls || [],
            },
          ],
        };
      }
    }

    return response;
  } catch (error) {
    // Xử lý lỗi cụ thể
    if (error.code === "ECONNABORTED") {
      throw new Error(
        "Yêu cầu tạo bài viết đã hết thời gian chờ. Vui lòng thử lại với nội dung ngắn hơn hoặc ít ảnh hơn."
      );
    }

    // Kiểm tra nếu có thông báo lỗi từ backend
    if (error.response && error.response.data) {
      // Nếu có thông báo lỗi DAILY_LIMIT_REACHED, truyền nguyên error để xử lý ở tầng cao hơn
      if (error.response.data.error === "DAILY_LIMIT_REACHED") {
        throw error;
      }

      // Truyền thông báo lỗi từ backend
      if (error.response.data.message) {
        throw new Error(error.response.data.message);
      }
    }

    throw error;
  }
};

/**
 * Generate an image using Gemini AI
 * @param {string} promptText - Text prompt for image generation
 * @param {string} userId - User ID
 * @returns {Promise} - API response
 */
export const generateGeminiImage = async (promptText, userId) => {
  try {
    return await axios.post(
      `${process.env.REACT_APP_API_URL}/api/content/generate-image-with-gemini`,
      {
        prompt: promptText,
        userId,
      },
      {
        timeout: 60000, // 1 minute timeout
      }
    );
  } catch (error) {
    // Nếu có thông báo lỗi từ backend liên quan đến giới hạn, truyền nguyên error
    if (error.response && error.response.data) {
      // Kiểm tra nếu có lỗi liên quan đến giới hạn ảnh AI
      if (
        error.response.data.error &&
        (error.response.data.error === "USER_AI_IMAGE_LIMIT_REACHED" ||
          error.response.data.error === "GLOBAL_AI_IMAGE_LIMIT_REACHED" ||
          error.response.data.error === "GLOBAL_AI_IMAGE_LIMIT_DISABLED")
      ) {
        throw error;
      }

      // Truyền thông báo lỗi từ backend
      if (error.response.data.message) {
        throw new Error(error.response.data.message);
      }
    }

    throw error;
  }
};

/**
 * Fetch images from Serper based on query text
 * @param {string} queryText - Text to search for images
 * @param {string} userId - User ID
 * @returns {Promise} - API response
 */
export const fetchImagesFromSerper = async (queryText, userId) => {
  return await axios.post(
    `${process.env.REACT_APP_API_URL}/api/content/get-images-from-serper`,
    {
      query: queryText,
      userId,
    },
    {
      timeout: 30000, // 30 seconds timeout
    }
  );
};

/**
 * Generate a post from uploaded images
 * @param {Object} options - Post generation options
 * @param {string} userId - User ID
 * @returns {Promise} - API response
 */
export const generatePostFromImage = async (options, userId) => {
  // Kiểm tra và xử lý kích thước ảnh
  if (options.images && Array.isArray(options.images)) {
    // Giới hạn số lượng ảnh
    options.images = options.images.slice(0, 5); // Chỉ gửi tối đa 5 ảnh

    // Tính toán tổng kích thước ảnh
    let totalSize = 0;
    options.images.forEach((img) => {
      if (typeof img === "string") {
        totalSize += img.length;
      } else if (img.preview && typeof img.preview === "string") {
        totalSize += img.preview.length;
      }
    });

    // Nếu tổng kích thước vượt quá 10MB, giới hạn số lượng ảnh
    const maxTotalSize = 10 * 1024 * 1024; // 10MB
    if (totalSize > maxTotalSize) {
      console.warn(
        `Tổng kích thước ảnh (${(totalSize / (1024 * 1024)).toFixed(
          2
        )}MB) vượt quá giới hạn 10MB, sẽ giảm số lượng ảnh`
      );

      // Sắp xếp ảnh theo kích thước tăng dần
      const sortedImages = [...options.images].sort((a, b) => {
        const sizeA =
          typeof a === "string"
            ? a.length
            : a.preview && typeof a.preview === "string"
            ? a.preview.length
            : 0;
        const sizeB =
          typeof b === "string"
            ? b.length
            : b.preview && typeof b.preview === "string"
            ? b.preview.length
            : 0;
        return sizeA - sizeB;
      });

      // Chọn các ảnh nhỏ nhất cho đến khi tổng kích thước < 10MB
      let currentSize = 0;
      const limitedImages = [];
      for (const img of sortedImages) {
        const imgSize =
          typeof img === "string"
            ? img.length
            : img.preview && typeof img.preview === "string"
            ? img.preview.length
            : 0;
        if (currentSize + imgSize <= maxTotalSize) {
          limitedImages.push(img);
          currentSize += imgSize;
        } else {
          break;
        }
      }

      options.images = limitedImages;
    }
  }

  return await axios.post(
    `${process.env.REACT_APP_API_URL}/api/content/generate-post-from-image`,
    {
      options,
      userId,
    },
    {
      timeout: 120000, // 2 minutes timeout
    }
  );
};
